import { z } from 'zod';
import type { CountryToken } from '@sleed/bizcommerce-api';

/**
 * Country edit form. Mirrors the `CountryToken` DTO and the legacy admin grid's validation
 * (Core/client/admin/src/pages/System/Countries/index.js): nm ≤100, iso2 ≤2, si required.
 * `cls` is the classification (EnumToken id). `erpcd` is not shown in the grid but is carried
 * through so an update never wipes it — matching the legacy onDataGridRowUpdatingMergeUnchanged.
 */
export const countrySchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	iso2: z.string().trim().min(1, 'validation.required').max(2, 'validation.tooLong'),
	cls: z.number().nullable(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	erpcd: z.string().nullable()
});

export type CountryFormValues = z.infer<typeof countrySchema>;

export const EMPTY_COUNTRY: CountryFormValues = { nm: '', iso2: '', cls: null, iv: true, si: 0, erpcd: null };

export function tokenToForm(token: CountryToken): CountryFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		iso2: token.iso2 ?? '',
		cls: token.cls ?? null,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		erpcd: token.erpcd ?? null
	};
}

export function formToToken(form: CountryFormValues): CountryToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		iso2: form.iso2.trim(),
		cls: form.cls ?? undefined,
		iv: form.iv,
		si: form.si,
		erpcd: form.erpcd
	};
}
